import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Space, Upload, Typography, message,
  Form, Input, Row, Col, List, Popconfirm, Tooltip, Empty,
  Divider, Alert, Tag, Checkbox,
} from 'antd';
import {
  UploadOutlined, FilePdfOutlined, FileImageOutlined,
  FileWordOutlined, FileExcelOutlined, FileOutlined,
  EyeOutlined, DeleteOutlined, ClockCircleOutlined, ScissorOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { uploadDocument, addDocumentToCase, removeDocumentFromCase, updatePledgeReleaseCase, updatePledgeReleaseCaseStatus, submitVerificationDocuments, revertDocumentSubmission, fetchDocApprovalInstance, generateVerificationForm } from '../api';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import exifr from 'exifr';
import PhoneCaptureButton from '../../../components/PhoneCapture/PhoneCaptureButton';
import ImageCropModal from '../../../components/ImageEditor/ImageCropModal';

const { Text, Title } = Typography;
const { TextArea } = Input;

const FILE_ICONS = {
  pdf: <FilePdfOutlined style={{ color: '#cf1322', fontSize: 20 }} />,
  jpg: <FileImageOutlined style={{ color: '#1677ff', fontSize: 20 }} />,
  jpeg: <FileImageOutlined style={{ color: '#1677ff', fontSize: 20 }} />,
  png: <FileImageOutlined style={{ color: '#1677ff', fontSize: 20 }} />,
  webp: <FileImageOutlined style={{ color: '#1677ff', fontSize: 20 }} />,
  doc: <FileWordOutlined style={{ color: '#2f54eb', fontSize: 20 }} />,
  docx: <FileWordOutlined style={{ color: '#2f54eb', fontSize: 20 }} />,
  xls: <FileExcelOutlined style={{ color: '#389e0d', fontSize: 20 }} />,
  xlsx: <FileExcelOutlined style={{ color: '#389e0d', fontSize: 20 }} />,
};

const getFileIcon = (name) => {
  if (!name) return <FileOutlined style={{ fontSize: 20 }} />;
  const ext = name.split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || <FileOutlined style={{ fontSize: 20 }} />;
};

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp'];
const isCroppableImage = (doc) => {
  const name = doc?.name || doc?.url || '';
  const ext = name.split('?')[0].split('.').pop().toLowerCase();
  return IMAGE_EXTS.includes(ext);
};

const DOC_LABELS = {
  PLEDGE_DOCUMENT: 'Pledge Document',
  AADHAR: 'Aadhaar',
  PAN: 'PAN',
  VERIFICATION_FORM: 'Verification Form',
  CURRENT_ADDRESS_PROOF: 'Current Address Proof',
  CHEQUE: 'Cheque',
};

const DocumentUploadSection = ({ caseId, caseData, documents = [], onDocumentsChanged, disabled = false, onNextTab }) => {
  const { user } = useAuth();
  const currentUserId = user?.id || user?._id;
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [pendingInstance, setPendingInstance] = useState(null);
  const [croppingDoc, setCroppingDoc] = useState(null);

  const [isAddressSameAsAadhaar, setIsAddressSameAsAadhaar] = useState(true);
  const [homeVisitImages, setHomeVisitImages] = useState([]);
  const [homeVisitDone, setHomeVisitDone] = useState(false);

  const docVerification = caseData?.documentVerification || {};
  const dvStatus = docVerification.status || 'NOT_SUBMITTED';

  // Show upload form when NOT_SUBMITTED or REJECTED (and case is not externally disabled)
  const canSubmit = !disabled && ['NOT_SUBMITTED', 'REJECTED'].includes(dvStatus);

  const skipDocValidation = process.env.REACT_APP_SKIP_DOC_VALIDATION === 'true';

  const [uploading, setUploading] = useState({
    pledgeDoc: false,
    aadharDoc: false,
    panDoc: false,
    formDoc: false,
  });

  // Hydrate the form from the server only when the case itself changes or its
  // verification status moves. Uploading a document refetches the case, and
  // re-hydrating on every refetch would wipe everything the user has typed but
  // not yet submitted (form fields, address checkbox, home-visit images).
  const hydrationKeyRef = useRef(null);

  useEffect(() => {
    if (caseData) {
      const hydrationKey = `${caseData._id}|${caseData.documentVerification?.status || 'NOT_SUBMITTED'}`;
      if (hydrationKeyRef.current === hydrationKey) return;
      hydrationKeyRef.current = hydrationKey;

      setIsAddressSameAsAadhaar(caseData.isAddressSameAsAadhaar ?? true);
      setHomeVisitImages(caseData.homeVisitImages || []);
      setHomeVisitDone(caseData.homeVisitDone || false);

      form.setFieldsValue({
        currentAddress: caseData.currentAddress || '',
      });
    }
  }, [caseData, form]);

  useEffect(() => {
    if (dvStatus !== 'PENDING_REVIEW' || !caseId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchDocApprovalInstance(caseId);
        if (!cancelled) setPendingInstance(res?.data || null);
      } catch {
        // non-fatal — banner still renders without approver details
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, dvStatus]);

  const handleRevert = async () => {
    setReverting(true);
    try {
      await revertDocumentSubmission(caseId);
      messageApi.success('Document submission reverted. You can re-upload and resubmit.');
      setPendingInstance(null);
      onDocumentsChanged?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to revert submission');
    } finally {
      setReverting(false);
    }
  };

  const normalizedDocs = documents.map((doc, idx) => {
    if (typeof doc === 'string') return { url: doc, name: `Document ${idx + 1}`, key: idx };
    return { ...doc, key: idx };
  });

  const handleUpload = async (key, file, documentType) => {
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const folderPath = `/case-documents/${caseId}`;
      const result = await uploadDocument(file, folderPath);
      const docData = {
        url: result.data?.url || result.url,
        name: `${DOC_LABELS[documentType] || documentType}_${file.name}`,
        fileId: result.data?.fileId || result.fileId,
        thumbnailUrl: result.data?.thumbnailUrl || result.thumbnailUrl,
        filePath: result.data?.filePath || result.filePath,
        documentType,
      };
      await addDocumentToCase(caseId, docData);
      messageApi.success(`${DOC_LABELS[documentType] || 'Document'} uploaded successfully`);
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Upload error:', err);
      messageApi.error(`Failed to upload ${DOC_LABELS[documentType] || 'Document'}`);
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // A phone-captured document is already on ImageKit, so it skips uploadDocument
  // and is attached to the case directly.
  const handlePhoneDocument = async (key, documentType, incoming = []) => {
    const shot = incoming[incoming.length - 1];
    if (!shot) return;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      await addDocumentToCase(caseId, {
        url: shot.url,
        name: `${DOC_LABELS[documentType] || documentType}_${shot.name || 'photo.jpg'}`,
        fileId: shot.fileId,
        thumbnailUrl: shot.thumbnailUrl,
        filePath: shot.filePath,
        documentType,
      });
      messageApi.success(`${DOC_LABELS[documentType] || 'Document'} received from phone`);
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Phone document attach error:', err);
      messageApi.error(`Failed to attach ${DOC_LABELS[documentType] || 'Document'}`);
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // The home-visit shot must be geo-tagged. Coordinates come from the phone's
  // GPS at shutter time (the capture session sets requireGeo), which is a
  // stronger guarantee than EXIF — EXIF can come from any photo taken anywhere,
  // and phones increasingly strip it when handing a file to the browser.
  const handlePhoneHomeVisit = (incoming = []) => {
    const geoTagged = incoming.filter((f) => f.lat != null && f.lng != null);
    if (geoTagged.length === 0) {
      messageApi.error('Photo arrived without coordinates. Allow location on the phone and retake.');
      return;
    }
    setHomeVisitImages((prev) => [
      ...prev,
      ...geoTagged.map((f) => ({
        url: f.url,
        lat: Number(f.lat).toFixed(6),
        long: Number(f.lng).toFixed(6),
      })),
    ]);
    setHomeVisitDone(true);
    messageApi.success(`${geoTagged.length} geo-tagged photo(s) received from phone`);
  };

  const handleRemoveHomeVisitImage = (index) => {
    setHomeVisitImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setHomeVisitDone(false);
      return updated;
    });
  };

  const handleHomeVisitImageUpload = async (info) => {
    setUploading((prev) => ({ ...prev, homeVisit: true }));

    const nativeFile = info.file?.originFileObj || info.file || info;

    if (!nativeFile || !(nativeFile instanceof File || nativeFile instanceof Blob)) {
      console.error('Invalid file structure passed:', info);
      messageApi.error('File object could not be resolved. Please try again.');
      setUploading((prev) => ({ ...prev, homeVisit: false }));
      return;
    }

    const getExifLocation = async (f) => {
      try {
        const gps = await exifr.parse(f, {
          gps: true,
          tiff: true,
          ifd0: true,
          exif: true
        });
        if (gps && gps.latitude !== undefined && gps.longitude !== undefined) {
          return {
            lat: Number(gps.latitude).toFixed(6),
            lon: Number(gps.longitude).toFixed(6)
          };
        }
        return null;
      } catch (err) {
        console.error('exifr parse error:', err);
        return null;
      }
    };

    try {
      const location = await getExifLocation(nativeFile);
      if (!location) {
        messageApi.error('No GPS coordinates found in this image. Please upload a geo-tagged image.');
        setUploading((prev) => ({ ...prev, homeVisit: false }));
        return;
      }

      messageApi.success(`Coordinates Found: ${location.lat}, ${location.lon}`);

      const folderPath = `/case-documents/${caseId}`;
      const result = await uploadDocument(nativeFile, folderPath);
      const fileUrl = result.data?.url || result.url;

      setHomeVisitImages((prev) => [...prev, { url: fileUrl, lat: location.lat, long: location.lon }]);
      setHomeVisitDone(true);
      messageApi.success('Home visit image uploaded successfully');

    } catch (err) {
      console.error('Home visit image upload error:', err);
      messageApi.error('Failed to upload home visit image');
    } finally {
      setUploading((prev) => ({ ...prev, homeVisit: false }));
    }
  };

  const handleGenerateForm = async () => {
    try {
      const values = form.getFieldsValue();
      const payload = {
        isAddressSameAsAadhaar,
        currentAddress: values.currentAddress,
        homeVisitImages,
        homeVisitDone: homeVisitImages.length > 0,
      };

      const blob = await generateVerificationForm(caseId, payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Verification-Form-${caseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success('Form generated successfully');
    } catch (err) {
      console.error('Generate form error:', err);
      messageApi.error('Failed to generate form');
    }
  };

  const handleSubmit = async (values) => {
    const mandatoryTypes = ['PLEDGE_DOCUMENT', 'AADHAR', 'PAN', 'VERIFICATION_FORM', 'CHEQUE'];
    const mandatoryDocs = normalizedDocs.filter(d => mandatoryTypes.includes(d.documentType));

    setSubmitting(true);
    try {
      const result = await submitVerificationDocuments(caseId, {
        documents: mandatoryDocs, // Optional submission
        isAddressSameAsAadhaar,
        currentAddress: values.currentAddress,
        homeVisitImages,
        homeVisitDone: homeVisitImages.length > 0,
      });

      if (result?.workflowWarning) {
        messageApi.warning(`Submitted, but: ${result.workflowWarning}`);
      } else {
        messageApi.info('Documents submitted for verification. Check Workflow → My Approvals.');
      }
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Submit error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to submit documents');
    } finally {
      setSubmitting(false);
    }
  };

  // Replace a document with its cropped version. Add first, then remove the old
  // entry: appending leaves the old index valid, and a failed add would
  // otherwise have already destroyed the only copy.
  const handleCroppedDocument = async (doc, fileRef) => {
    setCroppingDoc(null);
    setRemoving(doc.key);
    try {
      await addDocumentToCase(caseId, {
        url: fileRef.url,
        name: `${DOC_LABELS[doc.documentType] || doc.documentType}_${fileRef.name}`,
        fileId: fileRef.fileId,
        thumbnailUrl: fileRef.thumbnailUrl,
        filePath: fileRef.filePath,
        documentType: doc.documentType,
      });
      await removeDocumentFromCase(caseId, doc.key);
      messageApi.success('Document updated');
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Replace cropped document error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to update the document');
      onDocumentsChanged?.();
    } finally {
      setRemoving(null);
    }
  };

  const handleRemove = async (docIndex) => {
    setRemoving(docIndex);
    try {
      await removeDocumentFromCase(caseId, docIndex);
      messageApi.success('Document removed');
      onDocumentsChanged?.();
    } catch (err) {
      console.error('Remove document error:', err);
      messageApi.error(err?.response?.data?.message || 'Failed to remove document');
    } finally {
      setRemoving(null);
    }
  };

  const renderStatusBanner = () => {
    if (dvStatus === 'PENDING_REVIEW') {
      const currentStep = pendingInstance
        ? pendingInstance.steps?.find((s) => s.stepNumber === pendingInstance.currentStep)
        : null;
      const workflowName = pendingInstance?.template?.name || null;
      const approverDetails = currentStep?.approverDetails || [];
      const isApprover = currentUserId && approverDetails.some((a) => String(a._id) === String(currentUserId));
      const approverNames = approverDetails.length
        ? approverDetails.map((a) => a.name || a.email || a._id).join(', ')
        : null;

      const mandatoryDocs = normalizedDocs.filter((d) =>
        ['PLEDGE_DOCUMENT', 'AADHAR', 'PAN', 'VERIFICATION_FORM'].includes(d.documentType)
      );

      if (isApprover) {
        return (
          <div style={{ marginBottom: 16 }}>
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message="Pending Your Review"
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span>Submitted on {docVerification.submittedAt ? dayjs(docVerification.submittedAt).format('DD MMM YYYY, hh:mm A') : '—'}.</span>
                  {workflowName && <span><Text strong>Workflow: </Text>{workflowName}</span>}
                  {currentStep && <span><Text strong>Step: </Text>{currentStep.stepName}</span>}
                </div>
              }
            />
          </div>
        );
      }

      return (
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
          message="Documents Pending Verification"
          description={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>
                Submitted on {docVerification.submittedAt
                  ? dayjs(docVerification.submittedAt).format('DD MMM YYYY, hh:mm A')
                  : '—'}.
              </span>
              {workflowName && <span><Text strong>Workflow: </Text>{workflowName}</span>}
              {currentStep && <span><Text strong>Step: </Text>{currentStep.stepName}</span>}
              {approverNames && <span><Text strong>Pending with: </Text>{approverNames}</span>}
              <div style={{ marginTop: 8 }}>
                <Popconfirm
                  title="Revert document submission?"
                  description="This will cancel the pending approval and allow you to re-upload documents."
                  onConfirm={handleRevert}
                  okText="Yes, Revert"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" danger loading={reverting}>
                    Revert Submission
                  </Button>
                </Popconfirm>
              </div>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (dvStatus === 'APPROVED') {
      return (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="Documents Verified"
          description={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>
                Approved on {docVerification.approvedAt
                  ? dayjs(docVerification.approvedAt).format('DD MMM YYYY, hh:mm A')
                  : '—'}.
              </span>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (dvStatus === 'REJECTED') {
      return (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message="Documents Rejected — Resubmission Required"
          description={
            <span>
              {docVerification.rejectionReason || 'Reason not provided.'}{' '}
              Please correct and resubmit the documents below.
            </span>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {contextHolder}

      {/* {skipDocValidation && (
        <Alert
          type="warning"
          banner
          message="DEV MODE — document upload validation is disabled (REACT_APP_SKIP_DOC_VALIDATION=true). Any subset of documents can be submitted."
          style={{ marginBottom: 12 }}
        />
      )} */}

      <ImageCropModal
        open={Boolean(croppingDoc)}
        image={croppingDoc}
        folderPath={`/case-documents/${caseId}`}
        onCancel={() => setCroppingDoc(null)}
        onCropped={(fileRef) => handleCroppedDocument(croppingDoc, fileRef)}
      />

      {renderStatusBanner()}

      <Card
        title={
          <Space>
            {dvStatus === 'REJECTED' && <ReloadOutlined style={{ color: '#cf1322' }} />}
            {dvStatus === 'REJECTED' ? 'Resubmit Verification Documents' : 'Document Verification Form'}
          </Space>
        }
        size="small"
        style={{ marginBottom: 24, background: '#fafafa' }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={!canSubmit || submitting}>
          {/* Additional details, bank details and gold ownership are captured on
              the case itself (create / edit); the verification form PDF prints
              them from the saved case. */}
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Reason for selling, reference, bank and gold ownership details come from the case — edit the case to change them.
          </Text>
          <Title level={5}>Address & Home Visit</Title>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24}>
              <Checkbox
                checked={isAddressSameAsAadhaar}
                onChange={(e) => setIsAddressSameAsAadhaar(e.target.checked)}
                disabled={!canSubmit}
              >
                Customer current address is same as mentioned in Aadhaar
              </Checkbox>
            </Col>
          </Row>

          {!isAddressSameAsAadhaar && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="currentAddress"
                  label="Current Address"
                >
                  <TextArea rows={2} placeholder="Enter current address..." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: 30 }}>
                  {(() => {
                    const uploadedDoc = normalizedDocs.find(d => d.documentType === 'CURRENT_ADDRESS_PROOF');
                    const isUploading = uploading['addressProof'];
                    
                    return (
                      <>
                        {canSubmit && !uploadedDoc && (
                          <Upload
                            customRequest={({ file }) => handleUpload('addressProof', file, 'CURRENT_ADDRESS_PROOF')}
                            showUploadList={false}
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            disabled={!canSubmit || isUploading}
                          >
                            <Button loading={isUploading} icon={<UploadOutlined />}>
                              Upload Address Proof
                            </Button>
                          </Upload>
                        )}
                        {canSubmit && !uploadedDoc && (
                          <div style={{ marginTop: 8 }}>
                            <PhoneCaptureButton
                              targetKey="pledgeCase.document.CURRENT_ADDRESS_PROOF"
                              targetLabel="Current Address Proof — Pledge Case"
                              folderPath={`/case-documents/${caseId}`}
                              recordId={caseId}
                              slot="CURRENT_ADDRESS_PROOF"
                              maxFiles={1}
                              onFiles={(files) => handlePhoneDocument('addressProof', 'CURRENT_ADDRESS_PROOF', files)}
                              disabled={!canSubmit || isUploading}
                              buttonText="Capture from Phone (QR)"
                              buttonProps={{ size: 'small' }}
                            />
                          </div>
                        )}
                        {!canSubmit && !uploadedDoc && (
                          <Text type="secondary" style={{ fontSize: 12 }}>Address proof not uploaded</Text>
                        )}

                        {uploadedDoc && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                            <Space align="start">
                              {getFileIcon(uploadedDoc.name)}
                              <div style={{ wordBreak: 'break-all', fontSize: 14 }}>
                                <a href={uploadedDoc.url} target="_blank" rel="noopener noreferrer">
                                  {uploadedDoc.name || 'View Address Proof'}
                                </a>
                              </div>
                            </Space>
                            {canSubmit && dvStatus !== 'APPROVED' && (
                              <Space size={4}>
                                {isCroppableImage(uploadedDoc) && (
                                  <Button
                                    size="small"
                                    icon={<ScissorOutlined />}
                                    onClick={() => setCroppingDoc(uploadedDoc)}
                                    disabled={removing === uploadedDoc.key}
                                  >
                                    Crop
                                  </Button>
                                )}
                                <Popconfirm title="Remove this document?" onConfirm={() => handleRemove(uploadedDoc.key)} okText="Yes" cancelText="No">
                                  <Button size="small" danger icon={<DeleteOutlined />} loading={removing === uploadedDoc.key}>Remove</Button>
                                </Popconfirm>
                              </Space>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </Col>
            </Row>
          )}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 500, marginBottom: 12 }}>Geo-tagged Home Visit Image</div>
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    handleHomeVisitImageUpload(file);
                    return false;
                  }}
                  showUploadList={false}
                  accept=".jpg,.jpeg,.png,.webp"
                  disabled={!canSubmit || uploading.homeVisit}
                >
                  <Button loading={uploading.homeVisit} icon={<UploadOutlined />}>
                    Upload Home Visit Image
                  </Button>
                </Upload>

                <div style={{ marginTop: 8 }}>
                  <PhoneCaptureButton
                    targetKey="pledgeCase.homeVisit"
                    targetLabel="Geo-tagged Home Visit Photo"
                    folderPath={`/case-documents/${caseId}`}
                    recordId={caseId}
                    maxFiles={5}
                    requireGeo
                    onFiles={handlePhoneHomeVisit}
                    disabled={!canSubmit || uploading.homeVisit}
                    buttonText="Capture from Phone (QR)"
                    buttonProps={{ size: 'small' }}
                  />
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                    Capturing from the phone tags the photo with the phone's live GPS
                    position, so no geo-tagged gallery image is needed.
                  </div>
                </div>

                {homeVisitImages.length > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {homeVisitImages.map((img, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
                        <div>
                          <Tag color="green" icon={<CheckCircleOutlined />}>Home Visit Done</Tag>
                          {img.lat && img.long && (
                            <div style={{ marginTop: 4, fontSize: 12 }}>
                              <Text type="secondary">Lat: {img.lat}, Long: {img.long}</Text>
                            </div>
                          )}
                          <div style={{ marginTop: 4, fontSize: 12 }}>
                            <a href={img.url} target="_blank" rel="noopener noreferrer">View Image {idx + 1}</a>
                          </div>
                        </div>
                        {canSubmit && (
                          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveHomeVisitImage(idx)} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24}>
              <Button
                type="default"
                onClick={handleGenerateForm}
                icon={<FilePdfOutlined />}
                disabled={!canSubmit}
              >
                Generate Verification Form PDF
              </Button>
              <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                Staff and Customer will sign this generated form and upload it below.
              </Text>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />
          <Title level={5}>Mandatory Documents</Title>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {[
              { key: 'pledgeDoc', label: 'Pledge Document', type: 'PLEDGE_DOCUMENT' },
              { key: 'aadharDoc', label: 'Aadhaar Photo', type: 'AADHAR' },
              { key: 'panDoc', label: 'PAN Photo', type: 'PAN' },
              { key: 'formDoc', label: 'Form Photo', type: 'VERIFICATION_FORM' },
              { key: 'chequeDoc', label: 'Cheque', type: 'CHEQUE' },
            ].map(({ key, label, type }) => {
              const uploadedDoc = normalizedDocs.find(d => d.documentType === type);
              const isUploading = uploading[key];

              return (
                <Col xs={24} sm={12} md={6} key={key}>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontWeight: 500, marginBottom: 12 }}>{label}</div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {canSubmit && !uploadedDoc && (
                        <Upload
                          customRequest={({ file }) => handleUpload(key, file, type)}
                          showUploadList={false}
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          disabled={!canSubmit || isUploading || !!uploadedDoc}
                        >
                          <Button loading={isUploading} icon={<UploadOutlined />} disabled={!canSubmit || !!uploadedDoc}>
                            Upload {label}
                          </Button>
                        </Upload>
                      )}
                      {canSubmit && !uploadedDoc && (
                        <PhoneCaptureButton
                          targetKey={`pledgeCase.document.${type}`}
                          targetLabel={`${label} — Pledge Case`}
                          folderPath={`/case-documents/${caseId}`}
                          recordId={caseId}
                          slot={type}
                          maxFiles={1}
                          onFiles={(files) => handlePhoneDocument(key, type, files)}
                          disabled={!canSubmit || isUploading}
                          buttonText="Capture from Phone (QR)"
                          buttonProps={{ size: 'small', block: true }}
                        />
                      )}
                      {!canSubmit && !uploadedDoc && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Not uploaded</Text>
                      )}

                      {uploadedDoc && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Space align="start">
                            {getFileIcon(uploadedDoc.name)}
                            <div style={{ wordBreak: 'break-all', fontSize: 12, lineHeight: 1.2 }}>
                              <a href={uploadedDoc.url} target="_blank" rel="noopener noreferrer">
                                {uploadedDoc.name || 'View Document'}
                              </a>
                            </div>
                          </Space>
                          {canSubmit && dvStatus !== 'APPROVED' && (
                            <Space size={4}>
                              {isCroppableImage(uploadedDoc) && (
                                <Button
                                  size="small"
                                  icon={<ScissorOutlined />}
                                  onClick={() => setCroppingDoc(uploadedDoc)}
                                  disabled={removing === uploadedDoc.key}
                                >
                                  Crop
                                </Button>
                              )}
                              <Popconfirm title="Remove this document?" onConfirm={() => handleRemove(uploadedDoc.key)} okText="Yes" cancelText="No">
                                <Button size="small" danger icon={<DeleteOutlined />} loading={removing === uploadedDoc.key}>Remove</Button>
                              </Popconfirm>
                            </Space>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              )
            })}
          </Row>

          {canSubmit && (
            <Button type="primary" htmlType="submit" loading={submitting}>
              {dvStatus === 'REJECTED' ? 'Resubmit Documents' : 'Submit Verification Documents'}
            </Button>
          )}
        </Form>
      </Card>

      {normalizedDocs.filter(d => !['PLEDGE_DOCUMENT', 'AADHAR', 'PAN', 'VERIFICATION_FORM'].includes(d.documentType)).length > 0 && (
        <>
          <Title level={5}>Other Documents</Title>
          <List
            size="small"
            bordered
            dataSource={normalizedDocs.filter(d => !['PLEDGE_DOCUMENT', 'AADHAR', 'PAN', 'VERIFICATION_FORM'].includes(d.documentType))}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Tooltip title="View / Download" key="view">
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => doc.url && window.open(doc.url, '_blank')} disabled={!doc.url} />
                  </Tooltip>,
                  !disabled && dvStatus !== 'APPROVED' && (
                    <Popconfirm key="delete" title="Remove this document?" onConfirm={() => handleRemove(doc.key)} okText="Yes" cancelText="No">
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} loading={removing === doc.key} />
                    </Popconfirm>
                  ),
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={getFileIcon(doc.name)}
                  title={<Space size={4}><a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.name || 'Untitled'}</a>{doc.documentType && (<Tag color="blue" style={{ fontSize: 10 }}>{DOC_LABELS[doc.documentType] || doc.documentType}</Tag>)}</Space>}
                  description={doc.uploadedAt ? `Uploaded ${dayjs(doc.uploadedAt).format('DD MMM YYYY, hh:mm A')}` : null}
                />
              </List.Item>
            )}
          />
        </>
      )}
    </>
  );
};

export default DocumentUploadSection;
